import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeStyles } from 'tss-react/mui';

import type { WorkflowCardWorkflow } from './WorkflowCard';
import { WorkflowCard } from './WorkflowCard';
import { getWorkflowDndId } from '../utils/board-dnd-ids';

const useStyles = makeStyles()(theme => ({
  root: {
    padding: theme.spacing(0, 0.25),
  },
}));

interface SortableWorkflowCardProps extends React.HTMLAttributes<HTMLElement> {
  workflow: WorkflowCardWorkflow;
  disabled?: boolean;
  linkTo?: string;
}

export function SortableWorkflowCard({
  disabled,
  linkTo,
  workflow,
  ...rest
}: SortableWorkflowCardProps): JSX.Element {
  const { classes } = useStyles();
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    // Pass workflow data so it's available in drag event handlers
    data: { workflow },
    disabled,
    id: getWorkflowDndId(workflow.id),
  });

  // Placeholder styling when card is being dragged (ghost is shown via DragOverlay)
  const style: React.CSSProperties = {
    backgroundColor: isDragging ? '#F5F5F5' : undefined,
    border: isDragging ? '2px dashed #BDBDBD' : undefined,
    borderRadius: isDragging ? 8 : undefined,
    cursor: disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
    opacity: isDragging ? 0.4 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={classes.root}
      data-dragging={isDragging ? '' : undefined}
      style={style}
      {...attributes}
      {...listeners}
    >
      <WorkflowCard linkTo={linkTo} workflow={workflow} {...rest} />
    </div>
  );
}
